import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
	Box,
	Button,
	Flex,
	Heading,
	HStack,
	IconButton,
	LockClosedOutline,
	Modal,
	ModalBody,
	ModalContent,
	ModalOverlay,
	SimpleGrid,
	StarOutline,
	StarSolid,
	Text,
	Tooltip,
	XOutline,
} from '@bonusly/design-system';

import { promptGradientBorderSx } from '../../bizy/prompt-gradient';
import { GithubLogo } from '../../github/github-logo';
import { GranolaLogo } from '../../granola/granola-logo';
import { LinearLogo } from '../../linear/linear-logo';
import { SlackLogo } from '../../slack/slack-logo';
import {
	promptLibraryPromptText,
	type PromptConnector,
	type PromptLibraryCategory,
	type PromptLibraryCategoryId,
	type PromptLibraryPrompt,
} from '../lib/prompt-library';
import { DiceIcon } from './dice-icon';

const FAVORITES_NAV_ID = '__favorites__';
const SECTION_SPACING = 8; // 32px
const LAST_SECTION_TRAILING_SPACE = '84px';
const SCROLL_SPY_OFFSET = 1;
// Never taller than the viewport, so the body's overflow region stays reachable on short screens.
const PROMPT_LIBRARY_MODAL_MAX_H = 'calc(100dvh - 24px)';
const STICKY_HEADER_TOP_PADDING = 4; // 16px — masks content scrolling above the sticky title
// Distance (px) below which a programmatic scroll is treated as a no-op.
const PROGRAMMATIC_SCROLL_SETTLE_PX = 2;
// Treat the bottom of the list as the last section so short trailing sections
// (which can't scroll to the top) still highlight when scrolled into view.
const SCROLL_BOTTOM_THRESHOLD_PX = 4;

// "Surprise me" dice roll: a slot-machine cycle through prompts that decelerates
// and lands on a random pick before auto-submitting it to Bizy. Ticks are fixed
// (not tied to pool size) so the spin stays snappy regardless of catalog size.
const DICE_ROLL_TICKS = 10;
const DICE_ROLL_START_DELAY_MS = 50;
const DICE_ROLL_MAX_DELAY_MS = 200;
const DICE_ROLL_LANDED_PAUSE_MS = 600;
// After landing + celebrating, the prompt "launches" out of the modal before
// it's sent so it visually hands off to the chat bubble popping into view.
const DICE_LAUNCH_MS = 340;

// Celebration burst fired when the dice lands.
const CELEBRATION_EMOJIS = ['🎉', '✨', '🎊', '⭐️', '🥳', '🌟', '💫'];
const CELEBRATION_PARTICLE_COUNT = 16;

type ModalView = 'browse' | 'rolling';

type NavId = PromptLibraryCategoryId | typeof FAVORITES_NAV_ID;

const connectorLogos: Record<PromptConnector, React.FC<React.SVGProps<SVGSVGElement>>> = {
	github: GithubLogo,
	granola: GranolaLogo,
	linear: LinearLogo,
	slack: SlackLogo,
};

const connectorSourceKeys: Record<PromptConnector, string> = {
	github: 'promptLibrary.sources.github',
	granola: 'promptLibrary.sources.granola',
	linear: 'promptLibrary.sources.linear',
	slack: 'promptLibrary.sources.slack',
};

export interface BizyPromptLibraryModalProps {
	isOpen: boolean;
	onClose: () => void;
	categories: PromptLibraryCategory[];
	favoriteKeys: string[];
	onToggleFavorite: (prompt: PromptLibraryPrompt) => void;
	onSelectPrompt: (prompt: PromptLibraryPrompt, promptText: string) => void;
	// Invoked when a locked connector preview is clicked — routes the user to
	// Bizy connector settings so they can connect and unlock the prompt.
	onConnectConnector?: (connector: PromptConnector) => void;
	// Fired when the user starts a "Surprise me" dice roll (for analytics).
	onSurpriseMe?: () => void;
	promptName?: string;
}

interface RenderablePrompt {
	prompt: PromptLibraryPrompt;
	promptText: string;
}

interface NavSection {
	id: NavId;
	count: number;
	label: string;
	leadingIcon?: React.ReactNode;
}

function sectionElementId(navId: NavId) {
	return `prompt-library-section-${navId}`;
}

function getSectionScrollTop(container: HTMLElement, section: HTMLElement) {
	return section.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
}

function ConnectorFooter({ connector }: { connector?: PromptConnector }) {
	if (!connector) return null;

	const Logo = connectorLogos[connector];

	return (
		<Box position="absolute" left={3} bottom={2.5} color="text.softer" lineHeight={1} aria-hidden="true">
			<Logo width="0.95em" height="0.95em" />
		</Box>
	);
}

function LockedConnectorCard({ promptText, connector, onConnect }: { promptText: string; connector: PromptConnector; onConnect: () => void }) {
	const { t } = useTranslation('admin_bizy');
	const Logo = connectorLogos[connector];
	const connectorName = t(connectorSourceKeys[connector]);

	return (
		<Box h="100%">
			<Tooltip label={t('promptLibrary.connectToUse', { connector: connectorName })} placement="top" hasArrow={false}>
				<Box
					as="button"
					type="button"
					position="relative"
					display="flex"
					flexDirection="column"
					justifyContent="space-between"
					textAlign="left"
					w="100%"
					h="100%"
					borderRadius="12px"
					p={3}
					pb={2.5}
					minH="72px"
					cursor="pointer"
					transition="background 0.12s ease-out"
					bg="bg.subtle"
					_hover={{ bg: 'bg.canvas' }}
					_focusVisible={{
						outline: 'none',
						boxShadow: '0 0 0 2px var(--chakra-colors-primary-fg)',
					}}
					onClick={onConnect}
				>
					<Text fontSize="sm" color="text.disabled" fontWeight="normal" lineHeight="short">
						{promptText}
					</Text>
					<HStack spacing={1.5} mt={2} color="text.interactive" lineHeight={1}>
						{connector === 'slack' ? <SlackLogo width="0.95em" height="0.95em" mono /> : <Logo width="0.95em" height="0.95em" />}
						<LockClosedOutline boxSize={3} />
						<Text fontSize="xs" fontWeight="medium" color="inherit">
							{t('promptLibrary.connectCta', { connector: connectorName })}
						</Text>
					</HStack>
				</Box>
			</Tooltip>
		</Box>
	);
}

function PromptCard({
	promptText,
	connector,
	locked,
	isFavorite,
	onSelect,
	onConnect,
	onToggleFavorite,
}: {
	promptText: string;
	connector?: PromptConnector;
	locked: boolean;
	isFavorite: boolean;
	onSelect: () => void;
	onConnect: () => void;
	onToggleFavorite: () => void;
}) {
	const { t } = useTranslation('admin_bizy');

	if (locked && connector) {
		return <LockedConnectorCard promptText={promptText} connector={connector} onConnect={onConnect} />;
	}

	return (
		<Box
			position="relative"
			h="100%"
			role="group"
			sx={{
				'&:focus-within [data-favorite-toggle]': { opacity: 1 },
			}}
		>
			<Box
				as="button"
				type="button"
				position="relative"
				display="flex"
				flexDirection="column"
				justifyContent="flex-start"
				textAlign="left"
				w="100%"
				h="100%"
				borderRadius="12px"
				p={3}
				pb={connector ? 7 : 3}
				pr={10}
				minH="72px"
				color="text.softest"
				cursor="pointer"
				transition="color 0.12s ease-out, background 0.12s ease-out, border-color 0.12s ease-out"
				sx={promptGradientBorderSx}
				_hover={{ color: 'text.default' }}
				_focusVisible={{
					outline: 'none',
					boxShadow: '0 0 0 2px var(--chakra-colors-primary-fg)',
					borderColor: 'primary.fg',
				}}
				onClick={onSelect}
			>
				<Text fontSize="sm" color="inherit" fontWeight="normal" lineHeight="short">
					{promptText}
				</Text>
				<ConnectorFooter connector={connector} />
			</Box>
			<Box
				data-favorite-toggle
				position="absolute"
				top={2}
				right={2}
				opacity={isFavorite ? 1 : 0}
				_groupHover={{ opacity: 1 }}
				_focusWithin={{ opacity: 1 }}
				transition="opacity 0.12s ease-out"
			>
				<Tooltip label={t(isFavorite ? 'promptLibrary.removeFavorite' : 'promptLibrary.addFavorite')} placement="top" hasArrow={false}>
					<IconButton
						aria-label={t(isFavorite ? 'promptLibrary.removeFavorite' : 'promptLibrary.addFavorite')}
						aria-pressed={isFavorite}
						icon={isFavorite ? <StarSolid boxSize={4} /> : <StarOutline boxSize={4} />}
						variant="ghost"
						size="xs"
						borderRadius="full"
						color={isFavorite ? 'warning.fg' : 'text.softer'}
						_hover={{ bg: 'secondary.bgAlpha', color: isFavorite ? 'warning.fg' : 'text.default' }}
						onClick={(event) => {
							event.stopPropagation();
							onToggleFavorite();
						}}
					/>
				</Tooltip>
			</Box>
		</Box>
	);
}

function NavButton({
	isActive,
	count,
	leadingIcon,
	label,
	onClick,
}: {
	isActive: boolean;
	count: number;
	leadingIcon?: React.ReactNode;
	label: string;
	onClick: () => void;
}) {
	return (
		<Button
			variant="ghost"
			size="sm"
			justifyContent="space-between"
			flexShrink={0}
			h="auto"
			py={2}
			px={2.5}
			minH="32px"
			textAlign="left"
			fontSize="sm"
			fontWeight="medium"
			bg={isActive ? 'secondary.fg' : 'transparent'}
			color={isActive ? 'secondary.fgOnSurface' : 'text.default'}
			_hover={{ bg: isActive ? 'secondary.fg' : 'secondary.bgAlpha' }}
			_active={{ bg: isActive ? 'secondary.fg' : 'secondary.bgAlphaActive' }}
			aria-label={label}
			onClick={onClick}
		>
			<HStack spacing={2} minW={0} flex={1}>
				{leadingIcon}
				<Text noOfLines={1} fontWeight="inherit">
					{label}
				</Text>
			</HStack>
			<Text
				as="span"
				fontSize="xs"
				color={isActive ? 'secondary.fgOnSurface' : 'text.softest'}
				opacity={isActive ? 0.75 : 1}
				sx={{ fontVariantNumeric: 'tabular-nums' }}
				flexShrink={0}
				ml={2}
			>
				{count}
			</Text>
		</Button>
	);
}

function EmptyState({ icon, title, message }: { icon?: React.ReactNode; title: string; message?: string }) {
	return (
		<Flex direction="column" align="center" justify="center" py={10} px={4} gap={2} textAlign="center">
			{icon && (
				<Box color="text.softer" mb={1}>
					{icon}
				</Box>
			)}
			<Text fontSize="md" fontWeight="medium" color="text.default">
				{title}
			</Text>
			{message && (
				<Text fontSize="sm" color="text.soft" maxW="320px">
					{message}
				</Text>
			)}
		</Flex>
	);
}

function PromptGrid({
	prompts,
	favoriteKeySet,
	onSelectPrompt,
	onConnectConnector,
	onToggleFavorite,
}: {
	prompts: RenderablePrompt[];
	favoriteKeySet: Set<string>;
	onSelectPrompt: (prompt: PromptLibraryPrompt, promptText: string) => void;
	onConnectConnector?: (connector: PromptConnector) => void;
	onToggleFavorite: (prompt: PromptLibraryPrompt) => void;
}) {
	return (
		<SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} alignItems="stretch">
			{prompts.map(({ prompt, promptText }) => (
				<PromptCard
					key={`${prompt.categoryId}:${prompt.key}`}
					promptText={promptText}
					connector={prompt.connector}
					locked={prompt.locked}
					isFavorite={favoriteKeySet.has(prompt.key)}
					onSelect={() => onSelectPrompt(prompt, promptText)}
					onConnect={() => prompt.connector && onConnectConnector?.(prompt.connector)}
					onToggleFavorite={() => onToggleFavorite(prompt)}
				/>
			))}
		</SimpleGrid>
	);
}

function PromptLibrarySection({
	navId,
	title,
	description,
	stickyZIndex,
	sectionRef,
	hasDivider = false,
	children,
}: {
	navId: NavId;
	title: string;
	description?: string;
	stickyZIndex: number;
	sectionRef: (element: HTMLElement | null) => void;
	hasDivider?: boolean;
	children: React.ReactNode;
}) {
	return (
		<Box
			ref={sectionRef}
			id={sectionElementId(navId)}
			mb={SECTION_SPACING}
			pt={hasDivider ? 4 : 0}
			borderTop={hasDivider ? '1px solid' : undefined}
			borderColor="border.soft"
			scrollMarginTop={`${SCROLL_SPY_OFFSET}px`}
			_last={{ mb: LAST_SECTION_TRAILING_SPACE }}
		>
			<Box
				position="sticky"
				top={0}
				zIndex={stickyZIndex}
				bg="bg.surface"
				pt={STICKY_HEADER_TOP_PADDING}
				pb={3}
				pr={12}
				mx={-5}
				px={5}
				boxShadow="0 calc(var(--chakra-space-4) * -1) 0 var(--chakra-space-4) var(--chakra-colors-bg-surface)"
			>
				<Heading as="h2" size="xl" color="text.default">
					{title}
				</Heading>
				{description && (
					<Text mt={1} fontSize="sm" color="text.soft">
						{description}
					</Text>
				)}
			</Box>
			<Box pt={1}>{children}</Box>
		</Box>
	);
}

// Soft radial flash that blooms out from behind the die on landing.
function CelebrationGlow() {
	return (
		<Box
			position="absolute"
			inset="-60px"
			borderRadius="full"
			pointerEvents="none"
			aria-hidden="true"
			zIndex={0}
			bgImage="radial-gradient(circle, var(--chakra-colors-primary-bgAlpha) 0%, transparent 68%)"
			sx={{
				'@keyframes celebrateGlow': {
					'0%': { opacity: 0, transform: 'scale(0.5)' },
					'35%': { opacity: 1 },
					'100%': { opacity: 0, transform: 'scale(1.5)' },
				},
				animation: 'celebrateGlow 0.75s ease-out forwards',
			}}
		/>
	);
}

// Emoji particles that burst radially outward from the die center on landing.
function CelebrationBurst() {
	const particles = useMemo(
		() =>
			Array.from({ length: CELEBRATION_PARTICLE_COUNT }, (_, i) => {
				const angle = (Math.PI * 2 * i) / CELEBRATION_PARTICLE_COUNT + (Math.random() - 0.5) * 0.5;
				const distance = 80 + Math.random() * 70;
				return {
					id: i,
					emoji: CELEBRATION_EMOJIS[i % CELEBRATION_EMOJIS.length],
					tx: Math.cos(angle) * distance,
					ty: Math.sin(angle) * distance,
					rotate: (Math.random() - 0.5) * 220,
					delay: Math.random() * 80,
				};
			}),
		[]
	);

	return (
		<Box
			position="absolute"
			inset={0}
			pointerEvents="none"
			aria-hidden="true"
			zIndex={2}
			sx={{
				'@keyframes celebrateParticle': {
					'0%': { opacity: 0, transform: 'translate(-50%, -50%) translate(0, 0) scale(0.3) rotate(0deg)' },
					'15%': { opacity: 1 },
					'100%': { opacity: 0, transform: 'translate(-50%, -50%) translate(var(--tx), var(--ty)) scale(1) rotate(var(--rot))' },
				},
			}}
		>
			{particles.map((particle) => (
				<Box
					key={particle.id}
					position="absolute"
					left="50%"
					top="50%"
					fontSize="lg"
					lineHeight={1}
					style={
						{
							'--tx': `${particle.tx}px`,
							'--ty': `${particle.ty}px`,
							'--rot': `${particle.rotate}deg`,
							animation: `celebrateParticle 0.9s ease-out ${particle.delay}ms forwards`,
						} as Record<string, string> as React.CSSProperties
					}
				>
					{particle.emoji}
				</Box>
			))}
		</Box>
	);
}

function DiceRollingView({
	promptText,
	hasLanded,
	isLaunching,
	onClose,
}: {
	promptText: string;
	hasLanded: boolean;
	isLaunching: boolean;
	onClose: () => void;
}) {
	const { t } = useTranslation('admin_bizy');

	return (
		<Flex
			direction="column"
			flex={1}
			minW={0}
			minH={0}
			position="relative"
			bg="bg.surface"
			align="center"
			justify="center"
			px={6}
			py={10}
			textAlign="center"
			overflow="hidden"
		>
			<IconButton
				aria-label={t('sidePanel.close')}
				icon={<XOutline boxSize={5} />}
				variant="ghost"
				size="sm"
				position="absolute"
				top={4}
				right={5}
				bg="secondary.bgAlpha"
				borderRadius="full"
				color="secondary.fg"
				_hover={{ bg: 'secondary.bgAlphaHover' }}
				_active={{ bg: 'secondary.bgAlphaActive' }}
				onClick={onClose}
			/>
			<Flex
				direction="column"
				align="center"
				w="100%"
				sx={{
					// Little crouch, then a fast launch up + shrink as it "flies" into
					// the chat. Ease-in so it accelerates away.
					'@keyframes diceLaunch': {
						'0%': { transform: 'translateY(0) scale(1)', opacity: 1 },
						'28%': { transform: 'translateY(16px) scale(0.95)', opacity: 1 },
						'100%': { transform: 'translateY(-280px) scale(0.3)', opacity: 0 },
					},
					animation: isLaunching ? 'diceLaunch 0.34s cubic-bezier(0.5, 0, 0.75, 0) forwards' : undefined,
				}}
			>
				<Box position="relative" display="flex" alignItems="center" justifyContent="center">
					{hasLanded && <CelebrationGlow />}
					<Box
						position="relative"
						zIndex={1}
						color="primary.fg"
						lineHeight={0}
						sx={{
							'@keyframes diceRollSpin': {
								'0%': { transform: 'rotate(0deg) scale(1)' },
								'50%': { transform: 'rotate(180deg) scale(1.12)' },
								'100%': { transform: 'rotate(360deg) scale(1)' },
							},
							'@keyframes diceLandPop': {
								'0%': { transform: 'scale(1) rotate(0deg)' },
								'30%': { transform: 'scale(1.35) rotate(-9deg)' },
								'55%': { transform: 'scale(0.9) rotate(6deg)' },
								'75%': { transform: 'scale(1.14) rotate(-3deg)' },
								'100%': { transform: 'scale(1) rotate(0deg)' },
							},
							animation: hasLanded ? 'diceLandPop 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)' : 'diceRollSpin 0.6s linear infinite',
						}}
					>
						<DiceIcon width="48" height="48" />
					</Box>
					{hasLanded && <CelebrationBurst />}
				</Box>
				<Heading as="h2" size="lg" color="text.default" mt={6}>
					{hasLanded ? t('promptLibrary.surpriseSending') : t('promptLibrary.surpriseTitle')}
				</Heading>
				<Box
					mt={5}
					w="100%"
					maxW="420px"
					minH="72px"
					borderRadius="12px"
					p={4}
					display="flex"
					alignItems="center"
					justifyContent="center"
					color={hasLanded ? 'text.default' : 'text.soft'}
					transition="color 0.15s ease-out"
					sx={promptGradientBorderSx}
				>
					<Text key={promptText} fontSize="md" fontWeight={hasLanded ? 'medium' : 'normal'} lineHeight="short">
						{promptText}
					</Text>
				</Box>
			</Flex>
		</Flex>
	);
}

export function BizyPromptLibraryModal({
	isOpen,
	onClose,
	categories,
	favoriteKeys,
	onToggleFavorite,
	onSelectPrompt,
	onConnectConnector,
	onSurpriseMe,
	promptName,
}: BizyPromptLibraryModalProps) {
	const { t } = useTranslation('admin_bizy');
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const sectionElementsRef = useRef<Partial<Record<NavId, HTMLElement>>>({});
	// True while a click-driven programmatic scroll is in progress. Cleared by
	// any direct user scroll gesture (wheel/touch/keyboard) so the spy resumes
	// tracking immediately when the user takes over.
	const isProgrammaticScrollRef = useRef(false);
	const prevIsOpenRef = useRef(false);

	// "Surprise me" dice roll state.
	const [view, setView] = useState<ModalView>('browse');
	const [rollPool, setRollPool] = useState<RenderablePrompt[]>([]);
	const [rollIndex, setRollIndex] = useState(0);
	const [hasLanded, setHasLanded] = useState(false);
	const [isLaunching, setIsLaunching] = useState(false);
	const rollTimerRef = useRef<number>();

	const favoriteKeySet = useMemo(() => new Set(favoriteKeys), [favoriteKeys]);

	const favoritePrompts: RenderablePrompt[] = useMemo(
		() =>
			categories
				.flatMap((category) => category.prompts)
				.filter((prompt) => favoriteKeySet.has(prompt.key))
				.map((prompt) => ({ prompt, promptText: promptLibraryPromptText(prompt, t, promptName) })),
		[categories, favoriteKeySet, promptName, t]
	);

	const hasFavorites = favoritePrompts.length > 0;

	const navSections: NavSection[] = useMemo(() => {
		const sections: NavSection[] = [];

		if (hasFavorites) {
			sections.push({
				id: FAVORITES_NAV_ID,
				count: favoritePrompts.length,
				label: t('promptLibrary.favoritesCategory'),
				leadingIcon: <StarSolid boxSize={3.5} color="warning.fg" />,
			});
		}

		for (const category of categories) {
			sections.push({
				id: category.id,
				count: category.prompts.length,
				label: t(category.labelKey),
			});
		}

		return sections;
	}, [categories, favoritePrompts.length, hasFavorites, t]);

	const [activeNavId, setActiveNavId] = useState<NavId>(() => navSections[0]?.id ?? categories[0]?.id);

	const registerSectionRef = useCallback((navId: NavId) => {
		return (element: HTMLElement | null) => {
			if (element) {
				sectionElementsRef.current[navId] = element;
				return;
			}
			delete sectionElementsRef.current[navId];
		};
	}, []);

	const scrollEndTimerRef = useRef<number>();

	const scrollToSection = useCallback((navId: NavId, behavior: ScrollBehavior = 'smooth') => {
		const container = scrollContainerRef.current;
		const section = sectionElementsRef.current[navId];
		if (!container || !section) return;

		const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight);
		const targetTop = Math.min(Math.max(0, getSectionScrollTop(container, section) - SCROLL_SPY_OFFSET), maxScrollTop);

		setActiveNavId(navId);

		// Suppress the scroll spy for the duration of the programmatic scroll so
		// the highlight doesn't flicker through intermediate sections. The flag is
		// cleared by any direct user gesture (wheel/touch/key) or by the debounced
		// scroll-end timer in handleContainerScroll.
		window.clearTimeout(scrollEndTimerRef.current);
		const willAnimate = behavior === 'smooth' && Math.abs(container.scrollTop - targetTop) > PROGRAMMATIC_SCROLL_SETTLE_PX;
		isProgrammaticScrollRef.current = willAnimate;

		container.scrollTo({ top: targetTop, behavior });
	}, []);

	const updateActiveSectionFromScroll = useCallback(() => {
		const container = scrollContainerRef.current;
		if (!container || navSections.length === 0) return;

		// At (or near) the bottom, the last section is "current" even if its top
		// never reaches the spy line — otherwise short trailing sections never
		// highlight.
		const atBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - SCROLL_BOTTOM_THRESHOLD_PX;

		let nextActiveNavId = navSections[0].id;
		if (atBottom) {
			nextActiveNavId = navSections[navSections.length - 1].id;
		} else {
			const scrollPosition = container.scrollTop + SCROLL_SPY_OFFSET;
			for (const section of navSections) {
				const element = sectionElementsRef.current[section.id];
				if (!element) continue;
				if (getSectionScrollTop(container, element) <= scrollPosition) {
					nextActiveNavId = section.id;
				}
			}
		}

		setActiveNavId((current) => (current === nextActiveNavId ? current : nextActiveNavId));
	}, [navSections]);

	const handleContainerScroll = useCallback(() => {
		if (isProgrammaticScrollRef.current) {
			// Debounce: once scroll events stop arriving (smooth scroll finished),
			// clear the flag so that subsequent interactions (e.g. scrollbar drag)
			// can update the spy normally.
			window.clearTimeout(scrollEndTimerRef.current);
			scrollEndTimerRef.current = window.setTimeout(() => {
				isProgrammaticScrollRef.current = false;
			}, 100);
			return;
		}
		updateActiveSectionFromScroll();
	}, [updateActiveSectionFromScroll]);

	const handleUserScrollIntent = useCallback(() => {
		isProgrammaticScrollRef.current = false;
	}, []);

	// Every sendable prompt across categories (locked connector previews excluded —
	// they can't be submitted). This is the dice roll's pool.
	const selectablePrompts = useMemo<RenderablePrompt[]>(
		() =>
			categories
				.flatMap((category) => category.prompts)
				.filter((prompt) => !prompt.locked)
				.map((prompt) => ({ prompt, promptText: promptLibraryPromptText(prompt, t, promptName) })),
		[categories, promptName, t]
	);

	const startRoll = useCallback(() => {
		const pool = selectablePrompts;
		if (pool.length === 0) return;

		onSurpriseMe?.();
		const winner = Math.floor(Math.random() * pool.length);
		const submitWinner = () => onSelectPrompt(pool[winner].prompt, pool[winner].promptText);
		setRollPool(pool);
		setHasLanded(false);
		setIsLaunching(false);
		setView('rolling');
		window.clearTimeout(rollTimerRef.current);

		// Respect reduced-motion: skip the spin/launch, reveal the pick, then submit.
		const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
		if (prefersReducedMotion) {
			setRollIndex(winner);
			setHasLanded(true);
			rollTimerRef.current = window.setTimeout(submitWinner, DICE_ROLL_LANDED_PAUSE_MS);
			return;
		}

		// Land → celebrate → launch the prompt off → submit (which closes the modal
		// and lets the new chat bubble pop in, reading as one continuous handoff).
		const land = () => {
			setRollIndex(winner);
			setHasLanded(true);
			rollTimerRef.current = window.setTimeout(() => {
				setIsLaunching(true);
				rollTimerRef.current = window.setTimeout(submitWinner, DICE_LAUNCH_MS);
			}, DICE_ROLL_LANDED_PAUSE_MS);
		};

		const totalTicks = DICE_ROLL_TICKS;
		let tick = 0;
		let idx = Math.floor(Math.random() * pool.length);
		setRollIndex(idx);

		const step = () => {
			tick += 1;
			if (tick >= totalTicks) {
				land();
				return;
			}
			idx = (idx + 1) % pool.length;
			setRollIndex(idx);
			// Quadratic ease-out: ticks start fast and slow as the roll settles.
			const progress = tick / totalTicks;
			const delay = DICE_ROLL_START_DELAY_MS + (DICE_ROLL_MAX_DELAY_MS - DICE_ROLL_START_DELAY_MS) * progress * progress;
			rollTimerRef.current = window.setTimeout(step, delay);
		};

		rollTimerRef.current = window.setTimeout(step, DICE_ROLL_START_DELAY_MS);
	}, [selectablePrompts, onSelectPrompt, onSurpriseMe]);

	// Reset the dice view whenever the modal closes, and clear any pending timer
	// on unmount so a roll can't fire into an unmounted tree.
	useEffect(() => {
		if (!isOpen) {
			window.clearTimeout(rollTimerRef.current);
			setView('browse');
			setHasLanded(false);
			setIsLaunching(false);
		}
	}, [isOpen]);

	useEffect(
		() => () => {
			window.clearTimeout(rollTimerRef.current);
			window.clearTimeout(scrollEndTimerRef.current);
		},
		[]
	);

	useEffect(() => {
		const wasOpen = prevIsOpenRef.current;
		prevIsOpenRef.current = isOpen;

		if (!isOpen) return;

		if (isOpen && !wasOpen) {
			const initialNavId = hasFavorites ? FAVORITES_NAV_ID : categories[0]?.id;
			if (initialNavId) {
				setActiveNavId(initialNavId);
				requestAnimationFrame(() => {
					scrollToSection(initialNavId, 'instant');
				});
			}
		}
	}, [categories, hasFavorites, isOpen, scrollToSection]);

	useEffect(() => {
		if (!navSections.some((section) => section.id === activeNavId)) {
			setActiveNavId(navSections[0]?.id ?? categories[0]?.id);
		}
	}, [activeNavId, categories, navSections]);

	// Scroll/gesture listeners are bound via React props on the scroll container
	// (onScroll/onWheel/onTouchStart/onKeyDown) so they always attach to the
	// mounted node — manual addEventListener in an effect could miss the ref on
	// the first commit and never re-attach. This effect just syncs the highlight
	// once the section layout has settled after open / nav changes.
	useEffect(() => {
		if (!isOpen) return;
		const frame = requestAnimationFrame(() => {
			updateActiveSectionFromScroll();
		});
		return () => cancelAnimationFrame(frame);
	}, [isOpen, navSections, updateActiveSectionFromScroll]);

	if (categories.length === 0) return null;

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered scrollBehavior="outside">
			<ModalOverlay bg="bg.overlayBackdrop" />
			<ModalContent
				maxW={{ base: 'calc(100vw - 24px)', md: '880px' }}
				h={{ base: PROMPT_LIBRARY_MODAL_MAX_H, md: '600px' }}
				maxH={PROMPT_LIBRARY_MODAL_MAX_H}
				overflow="hidden"
				borderRadius="2xl"
				bg="bg.surface"
			>
				<ModalBody p={0} display="flex" flexDirection={{ base: 'column', md: 'row' }} minH={0}>
					{view === 'rolling' ? (
						<DiceRollingView promptText={rollPool[rollIndex]?.promptText ?? ''} hasLanded={hasLanded} isLaunching={isLaunching} onClose={onClose} />
					) : (
						<>
							<Flex
								w={{ base: '100%', md: '224px' }}
								flexShrink={0}
								direction="column"
								display={{ base: 'none', md: 'flex' }}
								borderRight="1px solid"
								borderColor="border.soft"
								bg="bg.surface"
								pt={4}
								px={3}
								pb={4}
								overflowY="auto"
							>
								<Box px={2} mb={4}>
									<Heading as="h1" size="xl" color="text.default">
										{t('promptLibrary.title')}
									</Heading>
								</Box>
								<Flex direction="column" gap={1}>
									{navSections.map((section) => (
										<NavButton
											key={section.id}
											isActive={activeNavId === section.id}
											count={section.count}
											leadingIcon={section.leadingIcon}
											label={section.label}
											onClick={() => scrollToSection(section.id)}
										/>
									))}
								</Flex>
							</Flex>
							<Flex direction="column" flex={1} minW={0} minH={0} position="relative" bg="bg.surface">
								<Box position="absolute" top={0} left={0} right={0} h={4} bg="bg.surface" zIndex={19} pointerEvents="none" aria-hidden="true" />
								<HStack spacing={1} position="absolute" top={4} right={5} zIndex={20}>
									<Tooltip label={t('promptLibrary.surpriseMe')} placement="bottom" hasArrow={false}>
										<IconButton
											aria-label={t('promptLibrary.surpriseMe')}
											icon={<DiceIcon width="20" height="20" />}
											variant="ghost"
											size="sm"
											bg="secondary.bgAlpha"
											borderRadius="full"
											color="secondary.fg"
											isDisabled={selectablePrompts.length === 0}
											_hover={{ bg: 'secondary.bgAlphaHover', color: 'primary.fg' }}
											_active={{ bg: 'secondary.bgAlphaActive' }}
											sx={{
												'@keyframes diceButtonSpin': {
													from: { transform: 'rotate(0deg)' },
													to: { transform: 'rotate(360deg)' },
												},
												'&:hover:not([disabled]) svg': {
													animation: 'diceButtonSpin 0.6s ease-in-out',
												},
											}}
											onClick={startRoll}
										/>
									</Tooltip>
									<IconButton
										aria-label={t('sidePanel.close')}
										icon={<XOutline boxSize={5} />}
										variant="ghost"
										size="sm"
										bg="secondary.bgAlpha"
										borderRadius="full"
										color="secondary.fg"
										_hover={{ bg: 'secondary.bgAlphaHover' }}
										_active={{ bg: 'secondary.bgAlphaActive' }}
										onClick={onClose}
									/>
								</HStack>
								<Box
									ref={scrollContainerRef}
									flex={1}
									overflowY="auto"
									px={5}
									pb={4}
									minH={0}
									position="relative"
									onScroll={handleContainerScroll}
									onWheel={handleUserScrollIntent}
									onTouchStart={handleUserScrollIntent}
									onKeyDown={handleUserScrollIntent}
								>
									{hasFavorites && (
										<PromptLibrarySection
											navId={FAVORITES_NAV_ID}
											title={t('promptLibrary.favoritesCategory')}
											description={t('promptLibrary.favoritesDescription')}
											stickyZIndex={2}
											sectionRef={registerSectionRef(FAVORITES_NAV_ID)}
											hasDivider={false}
										>
											{favoritePrompts.length === 0 ? (
												<EmptyState
													icon={<StarOutline boxSize={6} />}
													title={t('promptLibrary.favoritesEmpty')}
													message={t('promptLibrary.favoritesEmptyHint')}
												/>
											) : (
												<PromptGrid
													prompts={favoritePrompts}
													favoriteKeySet={favoriteKeySet}
													onSelectPrompt={onSelectPrompt}
													onConnectConnector={onConnectConnector}
													onToggleFavorite={onToggleFavorite}
												/>
											)}
										</PromptLibrarySection>
									)}
									{categories.map((category, index) => {
										const prompts = category.prompts.map((prompt) => ({
											prompt,
											promptText: promptLibraryPromptText(prompt, t, promptName),
										}));

										return (
											<PromptLibrarySection
												key={category.id}
												navId={category.id}
												title={t(category.labelKey)}
												description={t(category.descriptionKey)}
												stickyZIndex={3 + index}
												sectionRef={registerSectionRef(category.id)}
												hasDivider={hasFavorites || index > 0}
											>
												{prompts.length === 0 ? (
													<EmptyState title={t('promptLibrary.emptyCategory')} />
												) : (
													<PromptGrid
														prompts={prompts}
														favoriteKeySet={favoriteKeySet}
														onSelectPrompt={onSelectPrompt}
														onConnectConnector={onConnectConnector}
														onToggleFavorite={onToggleFavorite}
													/>
												)}
											</PromptLibrarySection>
										);
									})}
								</Box>
							</Flex>
						</>
					)}
				</ModalBody>
			</ModalContent>
		</Modal>
	);
}
